import { Injectable } from '@angular/core';
import { BehaviorSubject, merge, Subject } from 'rxjs';
import { share } from 'rxjs/operators';
import { PhxTableColumnConfig, PhxTableData, PhxStickyColumnInfo, PhxCellStyleInfo } from './phx-table.types';

@Injectable()
export class PhxTableService {
  private _columnsConfigSubject = new BehaviorSubject<PhxTableColumnConfig[]>([]);
  public columnsConfig$ = this._columnsConfigSubject.asObservable();

  private _selectedIdsSubject = new BehaviorSubject<ReadonlySet<number>>(new Set());
  public selectedIds$ = this._selectedIdsSubject.asObservable();

  private _disabledIdsSet = new Set<number>();

  private _stickyColumnsSubject = new BehaviorSubject<ReadonlyMap<number, PhxStickyColumnInfo>>(new Map());
  public stickyColumns$ = this._stickyColumnsSubject.asObservable();

  private _groupBorderColumnsSubject = new BehaviorSubject<ReadonlySet<number>>(new Set());
  public groupBorderColumns$ = this._groupBorderColumnsSubject.asObservable();

  /** Emits when style-related config changes. */
  public styleInvalidated$ = new Subject<void>();

  /** Pre-computed visible columns. */
  public visibleColumns: PhxTableColumnConfig[] = [];

  /** Pre-computed cell style map (columnKey → style info). */
  public cellStyleMap = new Map<string, PhxCellStyleInfo>();

  /** Fires when columns or styles change. */
  public rowStateChanged$ = merge(this.columnsConfig$, this.styleInvalidated$).pipe(share());

  public selectable = false;
  public clickable = false;

  public get columnsConfig(): PhxTableColumnConfig[] {
    return this._columnsConfigSubject.value;
  }

  public get selectedIds(): ReadonlySet<number> {
    return this._selectedIdsSubject.value;
  }

  public get disabledIds(): ReadonlySet<number> {
    return this._disabledIdsSet;
  }

  public get stickyColumns(): ReadonlyMap<number, PhxStickyColumnInfo> {
    return this._stickyColumnsSubject.value;
  }

  public get groupBorderColumns(): ReadonlySet<number> {
    return this._groupBorderColumnsSubject.value;
  }

  public setColumnsConfig(config: PhxTableColumnConfig[]): void {
    this._columnsConfigSubject.next(config);
    this.rebuildPreComputedState();
    this.styleInvalidated$.next();
  }

  public toggleRow(id: number): void {
    const next = new Set(this._selectedIdsSubject.value);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this._selectedIdsSubject.next(next);
  }

  public selectAll(data: PhxTableData[]): void {
    this._selectedIdsSubject.next(new Set(data.filter(d => !this._disabledIdsSet.has(d.id)).map(d => d.id)));
  }

  public setRowDisabled(id: number, disabled: boolean): void {
    if (disabled) {
      this._disabledIdsSet.add(id);
    } else {
      this._disabledIdsSet.delete(id);
    }
  }

  public clearSelection(): void {
    this._selectedIdsSubject.next(new Set());
  }

  public isSelected(id: number): boolean {
    return this._selectedIdsSubject.value.has(id);
  }

  public setGroupBorderColumns(rightBorders: ReadonlySet<number>): void {
    this._groupBorderColumnsSubject.next(rightBorders);
    this.rebuildPreComputedState();
    this.styleInvalidated$.next();
  }

  public setStickyColumns(map: ReadonlyMap<number, PhxStickyColumnInfo>): void {
    this._stickyColumnsSubject.next(map);
    this.rebuildPreComputedState();
    this.styleInvalidated$.next();
  }

  /** Rebuilds `visibleColumns` and `cellStyleMap`. */
  private rebuildPreComputedState(): void {
    const allColumns = this._columnsConfigSubject.value;
    this.visibleColumns = allColumns.filter(c => c.visible !== false);

    this.cellStyleMap.clear();
    const stickyMap = this._stickyColumnsSubject.value;
    const rightBorders = this._groupBorderColumnsSubject.value;

    allColumns.forEach((col, idx) => {
      this.cellStyleMap.set(col.key, {
        align: col.align,
        stickyInfo: stickyMap.get(idx),
        groupBorderRight: rightBorders.has(idx),
      });
    });
  }
}
